import { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import Accordion from '../../../Layout/Accordion/Accordion'
import { SurveyProps } from '@/app/models/survey'
import { LuxButton, LuxIcon, Color } from '@lux/wardrobe-lib'
import { isMobileChecker } from '@/app/utils/commonFunctions'
import DialogQualtrics from '@/app/components/UI/Dialogs/DialogQualtrics/DialogQualtrics'
import './Surveys.scss'
import { canExecuteSurveyTask } from '@Techedge/digitalportal-shared'
import { AttachmentProps } from '@/app/models/attachment'
import Attachment from '@/app/components/UI/Attachment/Attachment'
import useInterval from '@use-it/interval'
import { Markup } from 'interweave'

const Surveys = (props: {
    surveys: SurveyProps[]
    setSurveys: (surveys: SurveyProps[]) => void
    surveyUrlQueryString?: string
    onCloseDialog: () => void
    projectsDetails: any
    loadingSurveyStatus: boolean
    surveyStatusUpdateMap: any
    getSurveyStatus: (surveyId: string, storeId: string, projectId: string) => void
}) => {
    const {
        surveys,
        setSurveys,
        surveyUrlQueryString,
        onCloseDialog,
        projectsDetails,
        loadingSurveyStatus,
        surveyStatusUpdateMap,
        getSurveyStatus,
    } = props

    const intl = useIntl()

    const isMobile = isMobileChecker()

    const componentClass = 'smart-modals-news-execution-surveys'

    const [dialogQualtricsVisible, setDialogQualtricsVisible] = useState<boolean>(false)

    const [surveyUrl, setSurveyUrl] = useState<string>('')
    const [surveyTitle, setSurveyTitle] = useState<string>('')
    const [selectedSurveyCompleted, setSelectedSurveyCompleted] = useState<string>('')
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false)

    const [surveyId, setSurveyId] = useState('')
    const [storeId, setStoreId] = useState('')
    const [projectId, setProjectId] = useState('')

    useEffect(() => {
        setSurveyId(projectsDetails.surveys[0].surveyId)
        setStoreId(projectsDetails.storesList[0].storeId)
        setProjectId(projectsDetails.storesList[0].projectId)
    }, [projectsDetails])

    useEffect(() => {
        if (surveyStatusUpdateMap[surveyId]?.status === 'completed') {
            setIsUpdatingStatus(false)
            onCloseDialog()
        }
    }, [surveyStatusUpdateMap[surveyId], loadingSurveyStatus])

    useInterval(() => {
        if (isUpdatingStatus) {
            getSurveyStatus(surveyId, storeId, projectId)
        }
    }, 1000)

    return (
        <>
            <div className={componentClass}>
                {isMobile && (
                    <div className={`${componentClass}__info`}>
                        <div className={`${componentClass}__info__number_of`}>
                            <span>{`${intl.formatMessage({
                                id: 'modals.news_execution.project_specs.details.number_of',
                                defaultMessage: 'Number of',
                            })} ${intl.formatMessage({
                                id: 'modals.news_execution.action.action_card.title.surveys',
                                defaultMessage: 'Surveys',
                            })}`}</span>
                            <strong>{surveys?.length}</strong>
                        </div>
                    </div>
                )}
                {surveyStatusUpdateMap[surveyId]?.status === 'not_completed' ? (
                    <div className="smart-modals--loading" style={{ flexDirection: 'column' }}>
                        <LuxIcon icon="loading" color={Color.WHITE} />
                        <p style={{ color: 'white', fontSize: '15px' }}>
                            {intl.formatMessage({
                                id: 'survey.status.request',
                                defaultMessage: 'Please wait, status update in progress...',
                            })}
                        </p>
                    </div>
                ) : (
                    <></>
                )}
                {surveys?.length > 0 ? (
                    surveys.map((s: SurveyProps, i: number) => {
                        return (
                            <Accordion
                                id={i}
                                key={i}
                                title={s.title}
                                activeIndex={i}
                                className={s.status}>
                                <div className={`${componentClass}__survey`}>
                                    <div className={`${componentClass}__survey__description`}>
                                        <div>
                                            <Markup content={s.description} />
                                        </div>
                                    </div>
                                    <div className={`${componentClass}__survey__details`}>
                                        <div
                                            className={`${componentClass}__survey__details__detail`}>
                                            <strong>
                                                {intl.formatMessage({
                                                    id: 'modals.news_execution.action.tasks.detail.creation_date',
                                                    defaultMessage: 'Creation date',
                                                })}
                                            </strong>
                                            <span>{s.creation_date}</span>
                                        </div>
                                        <div
                                            className={`${componentClass}__survey__details__detail`}>
                                            <strong>
                                                {intl.formatMessage({
                                                    id: 'modals.news_execution.action.tasks.detail.end_date',
                                                    defaultMessage: 'End date',
                                                })}
                                            </strong>
                                            <span>{s.end_date}</span>
                                        </div>
                                        <div
                                            className={`${componentClass}__survey__details__detail`}>
                                            <strong>
                                                {intl.formatMessage({
                                                    id: 'modals.news_execution.action.tasks.detail.priority',
                                                    defaultMessage: 'Priority',
                                                })}
                                            </strong>
                                            <span>{s.priority}</span>
                                        </div>
                                        <div
                                            className={`${componentClass}__survey__details__detail`}>
                                            <strong>
                                                {intl.formatMessage({
                                                    id: 'modals.news_execution.action.tasks.detail.role',
                                                    defaultMessage: 'Role',
                                                })}
                                            </strong>
                                            <span>{s.role}</span>
                                        </div>
                                        {(canExecuteSurveyTask({
                                            status: s.storeStatus,
                                            module: 'communication',
                                            type: 'communication',
                                            assignment: s.assignment,
                                        }) ||
                                            s.completed) && (
                                            <div
                                                className={`${componentClass}__survey__details__detail`}>
                                                <strong className="bold">{s.survey_title}</strong>
                                                <LuxButton
                                                    variant={'outlined'}
                                                    disabled={
                                                        new Date().getTime() <
                                                        new Date(s.creation_date).getTime()
                                                    }
                                                    text={intl.formatMessage({
                                                        id: 'modals.news_execution.action.surveys.open_survey',
                                                        defaultMessage: 'Open Survey',
                                                    })}
                                                    onClick={() => {
                                                        setSurveyUrl(s.survey_url)
                                                        setSurveyTitle(s.survey_title)
                                                        setSelectedSurveyCompleted(
                                                            s.completed ||
                                                                new Date().getTime() <
                                                                    s.startDate ||
                                                                !s.hasAccepted
                                                                ? `?Q_R=${s.answer?.surveyResponse?.responseId}&readOnly=true`
                                                                : ''
                                                        )
                                                        setDialogQualtricsVisible(true)
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {s.attachments && (
                                            <div className={`${componentClass}__attachments`}>
                                                {s.attachments.map(
                                                    (a: AttachmentProps, i: number) => {
                                                        return (
                                                            <div key={i}>
                                                                {a && (
                                                                    <Attachment
                                                                        key={i}
                                                                        fileUrl={a?.url}
                                                                        fileName={a?.name}
                                                                        fileExtension={a?.ext}
                                                                        showDownloadIcon
                                                                        hideEyeIcon
                                                                    />
                                                                )}
                                                            </div>
                                                        )
                                                    }
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Accordion>
                        )
                    })
                ) : (
                    <div>
                        {intl.formatMessage({
                            id: 'modals.news_execution.action.surveys.no_surveys',
                            defaultMessage: 'No surveys.',
                        })}
                    </div>
                )}
            </div>
            <DialogQualtrics
                surveyUrl={`${surveyUrl}${selectedSurveyCompleted || surveyUrlQueryString}`}
                surveyTitle={surveyTitle}
                dialogVisible={dialogQualtricsVisible}
                onHide={() => {
                    setDialogQualtricsVisible(false)
                    onCloseDialog()
                }}
                onSubmit={() => {
                    setIsUpdatingStatus(true)
                }}
            />
        </>
    )
}

export default Surveys
